import React from 'react';
import { Box, Typography, Button, Alert, AlertTitle } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';

/**
 * Error boundary for section-level components (panels, cards, data sections)
 * Provides a more compact inline error message with retry functionality
 */
export default class SectionErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    if (process.env.NODE_ENV === 'production') {
      fetch('/api/log-client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          error: error.toString(),
          errorInfo,
          url: window.location.href,
          userAgent: navigator.userAgent,
          section: this.props.sectionName,
          timestamp: new Date().toISOString(),
        }),
      });
    } else {
      console.error(`[SectionErrorBoundary] (${this.props.sectionName})`, error, errorInfo);
    }
    if (this.props.onError) this.props.onError(error, errorInfo);
  }

  handleRetry = () => {
    if (this.props.onRetry) {
      this.props.onRetry();
    }
    this.setState({ hasError: false, error: null });
  };

  render() {
    const { sectionName, severity = 'error' } = this.props;

    if (this.state.hasError) {
      const { error } = this.state;
      const fallback = (
        <Alert
          severity={severity}
          sx={{
            width: '100%',
            my: 2,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <AlertTitle>{sectionName ? `${sectionName} Error` : 'Section Error'}</AlertTitle>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {error
                ? error.message
                  ? `Unexpected error in ${sectionName}: ${error.message}`
                  : `Unexpected error in ${sectionName}`
                : 'Unable to load this section. Please try again.'}
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<RefreshIcon />}
              onClick={this.handleRetry}
            >
              Retry
            </Button>
          </Box>
        </Alert>
      );
      return fallback;
    }

    return this.props.children;
  }
}
